import { useEffect, useRef, useState } from "react";
import { BookOpen, ChevronDown, ChevronRight, FileText, Layers, Library, Link2, ListTree, Loader2, Presentation, Trash2, Upload, X } from "lucide-react";
import { Dialog } from "@/components/modals";
import { useNotification } from "@/contexts/NotificationContext";
import { errorMessage } from "@/lib/api";
import { listMyMedia, resolveCanvaShare, uploadLearnMedia, type LearnMedia } from "@/lib/media";
import {
  SLIDE_FILE_ACCEPT,
  applyPicks,
  deckHasSource,
  emptySlideDeck,
  isCanvaShortLink,
  kindLabel,
  newDeckId,
  outlineKey,
  parseCanvaUrl,
  pdfViewerUrl,
  pickLabel,
  picksForMap,
  type OutlineMark,
  type OutlineNode,
  type OutlinePick,
  type SlideDeck,
  type SlideKind,
  type SlideMap,
  type SlideMode,
} from "@/lib/slideDecks";

function isSlideFile(file: LearnMedia) {
  const mime = (file.mimeType || "").toLowerCase();
  const name = (file.originalFilename || "").toLowerCase();
  return mime.includes("pdf") || /\.pdf$/.test(name);
}

const MODE_CARDS: Array<{
  id: SlideMode;
  title: string;
  subtext: string;
  icon: typeof Presentation;
}> = [
  {
    id: "activity",
    title: "This activity",
    subtext: "A deck that lives on this item only. Students open it here — nothing to map.",
    icon: Presentation,
  },
  {
    id: "map",
    title: "Map to the course outline",
    subtext: "One study shared across units or lessons. Map slide ranges onto the outline.",
    icon: ListTree,
  },
];

function emptyMap(): SlideMap {
  return { id: newDeckId(), from: 1, to: 5, targetKind: "container", targetId: "", targets: [] };
}

async function guessPdfPages(file: File) {
  if (!/\.pdf$/i.test(file.name) && file.type !== "application/pdf") return undefined;
  try {
    const buf = await file.arrayBuffer();
    const slice = buf.byteLength > 8_000_000 ? buf.slice(0, 8_000_000) : buf;
    const text = new TextDecoder("latin1").decode(slice);
    const count = (text.match(/\/Type\s*\/Page(?!s)\b/g) || []).length;
    return count >= 1 && count <= 999 ? count : undefined;
  } catch {
    return undefined;
  }
}

function Mark({ mark }: { mark: OutlineMark }) {
  return (
    <span className={`al-cb-deck-mark al-cb-deck-mark--${mark}`} aria-hidden="true">
      {mark}
    </span>
  );
}

function NodeIcon({ mark }: { mark: OutlineMark }) {
  const props = { size: 11, "aria-hidden": true as const };
  if (mark === "C") return <Library {...props} />;
  if (mark === "U") return <Layers {...props} />;
  if (mark === "L") return <BookOpen {...props} />;
  return <FileText {...props} />;
}

function OutlinePicker({
  outline,
  selected,
  disabled,
  onChange,
}: {
  outline: OutlineNode[];
  selected: OutlinePick[];
  disabled: boolean;
  onChange: (next: OutlinePick[]) => void;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const dragRef = useRef<{ start: number; keys: string[] } | null>(null);
  const [hoverKeys, setHoverKeys] = useState<string[] | null>(null);

  const selectedKeys = selected.map((pick) => outlineKey(pick.kind, pick.id));
  const activeKeys = hoverKeys || selectedKeys;

  const visible: Array<{ node: OutlineNode; depth: number; itemCount: number }> = [];
  const walk = (nodes: OutlineNode[], depth: number) => {
    for (const node of nodes) {
      const nested = node.children.filter((child) => child.kind !== "item");
      const items = node.children.filter((child) => child.kind === "item");
      visible.push({ node, depth, itemCount: items.length });
      walk(nested, depth + 1);
      if (items.length && expanded.has(node.id)) walk(items, depth + 1);
    }
  };
  walk(outline, 0);

  const picksFromKeys = (keys: string[]) =>
    keys
      .map((key) => visible.find((row) => outlineKey(row.node.kind, row.node.id) === key)?.node)
      .filter((node): node is OutlineNode => Boolean(node))
      .map((node) => ({ kind: node.kind, id: node.id, label: pickLabel(node), mark: node.mark }));

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: PointerEvent) => {
      if (dragRef.current) return;
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDoc);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDoc);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const rangeKeys = (from: number, to: number) => {
    const lo = Math.min(from, to);
    const hi = Math.max(from, to);
    return visible.slice(lo, hi + 1).map((row) => outlineKey(row.node.kind, row.node.id));
  };

  return (
    <div className="al-cb-outline-pick" ref={rootRef}>
      <div
        className={`al-cb-outline-pick-btn${open ? " al-cb-outline-pick-btn--on" : ""}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((value) => !value);
        }}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen((value) => !value);
          }
        }}
      >
        {selected.length ? (
          selected.map((pick) => (
            <span key={outlineKey(pick.kind, pick.id)} className="al-cb-outline-chip">
              <Mark mark={pick.mark || "I"} />
              <span className="al-cb-outline-chip-label">{pick.label}</span>
              {!disabled ? (
                <button
                  type="button"
                  className="al-cb-outline-chip-x"
                  aria-label={`Remove ${pick.label}`}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onChange(selected.filter((row) => outlineKey(row.kind, row.id) !== outlineKey(pick.kind, pick.id)));
                  }}
                >
                  <X size={9} />
                </button>
              ) : null}
            </span>
          ))
        ) : (
          <span className="al-cb-outline-pick-ph">Map to unit, lesson, or item…</span>
        )}
      </div>

      {open ? (
        <div className="al-cb-outline-panel" role="listbox" aria-label="Course outline">
          <p className="al-cb-outline-panel-hint">Click one, or drag across several.</p>
          {visible.map((row, index) => {
            const key = outlineKey(row.node.kind, row.node.id);
            const on = activeKeys.includes(key);
            return (
              <div
                key={key}
                className={`al-cb-outline-row al-cb-outline-row--${row.node.mark}${on ? " al-cb-outline-row--on" : ""}`}
                style={{ ["--d" as string]: row.depth }}
                role="option"
                aria-selected={on}
                onPointerDown={(event) => {
                  if (disabled || event.button !== 0) return;
                  event.preventDefault();
                  const keys = event.shiftKey
                    ? Array.from(new Set([...selectedKeys, key]))
                    : [key];
                  dragRef.current = { start: index, keys };
                  setHoverKeys(keys);
                  const onUp = () => {
                    window.removeEventListener("pointerup", onUp);
                    const drag = dragRef.current;
                    dragRef.current = null;
                    setHoverKeys(null);
                    if (!drag) return;
                    const picks = picksFromKeys(drag.keys);
                    if (picks.length) onChange(picks);
                    if (drag.keys.length <= 1) setOpen(false);
                  };
                  window.addEventListener("pointerup", onUp);
                }}
                onPointerEnter={() => {
                  const drag = dragRef.current;
                  if (!drag) return;
                  const keys = rangeKeys(drag.start, index);
                  drag.keys = keys;
                  setHoverKeys(keys);
                }}
              >
                {row.itemCount ? (
                  <button
                    type="button"
                    className="al-cb-outline-caret"
                    aria-label={expanded.has(row.node.id) ? "Hide items" : `Show ${row.itemCount} items`}
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      setExpanded((prev) => {
                        const next = new Set(prev);
                        if (next.has(row.node.id)) next.delete(row.node.id);
                        else next.add(row.node.id);
                        return next;
                      });
                    }}
                  >
                    {expanded.has(row.node.id) ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                  </button>
                ) : (
                  <span className="al-cb-outline-caret-spacer" />
                )}
                <Mark mark={row.node.mark} />
                <NodeIcon mark={row.node.mark} />
                <span className="al-cb-outline-row-text">
                  {row.node.kind !== "course" ? (
                    <span className="al-cb-outline-row-kicker">{row.node.label}</span>
                  ) : null}
                  <span className="al-cb-outline-row-title">{row.node.title}</span>
                </span>
                {row.itemCount && !expanded.has(row.node.id) ? (
                  <span className="al-cb-outline-item-n">{row.itemCount}</span>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default function SlideDeckEditor({
  decks,
  outline,
  mode,
  disabled,
  onChange,
  onModeChange,
}: {
  decks: SlideDeck[];
  outline: OutlineNode[];
  mode: SlideMode | null;
  disabled: boolean;
  onChange: (next: SlideDeck[]) => void;
  onModeChange: (mode: SlideMode) => void;
}) {
  const { notice } = useNotification();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const decksRef = useRef(decks);
  decksRef.current = decks;
  const canvaTimer = useRef<number | null>(null);
  const [uploadFor, setUploadFor] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [files, setFiles] = useState<LearnMedia[] | null>(null);

  const patch = (id: string, next: Partial<SlideDeck>) =>
    onChange(decksRef.current.map((deck) => (deck.id === id ? { ...deck, ...next } : deck)));

  const patchMap = (deck: SlideDeck, mapId: string, next: SlideMap) =>
    patch(deck.id, { maps: deck.maps.map((row) => (row.id === mapId ? next : row)) });

  const addDeck = () => onChange([...decks, emptySlideDeck()]);

  const chooseMode = (next: SlideMode) => {
    onModeChange(next);
    if (!decks.length) onChange([emptySlideDeck()]);
  };

  const lookupCanva = async (deckId: string, value: string) => {
    const parsed = parseCanvaUrl(value);
    if (parsed?.embed) {
      if (parsed.view !== value.trim()) patch(deckId, { url: parsed.view, mediaId: undefined });
      return;
    }
    if (!isCanvaShortLink(value) && !parsed?.short) return;
    setBusyId(deckId);
    try {
      const row = await resolveCanvaShare(value);
      patch(deckId, { url: row.url, mediaId: undefined });
    } catch (err) {
      notice({
        type: "error",
        title: "Could not embed that Canva link",
        subtext: errorMessage(err),
        duration: 7000,
      });
    } finally {
      setBusyId((current) => (current === deckId ? null : current));
    }
  };

  const onCanvaUrl = (deckId: string, value: string) => {
    patch(deckId, { url: value, mediaId: undefined });
    if (canvaTimer.current) window.clearTimeout(canvaTimer.current);
    canvaTimer.current = window.setTimeout(() => {
      void lookupCanva(deckId, value);
    }, 450);
  };

  const applyFile = (deck: SlideDeck, file: LearnMedia, slideCount?: number) => {
    patch(deck.id, {
      kind: "pdf",
      url: file.url,
      mediaId: file.id,
      title: deck.title || file.originalFilename.replace(/\.[^.]+$/, ""),
      ...(slideCount && !deck.slideCount ? { slideCount } : {}),
    });
    setPickerFor(null);
  };

  const pickFile = (file: LearnMedia) => {
    const deck = decksRef.current.find((row) => row.id === pickerFor);
    if (deck) applyFile(deck, file);
  };

  useEffect(() => {
    if (!pickerFor) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopImmediatePropagation();
      setPickerFor(null);
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [pickerFor]);

  const openPicker = async (deckId: string) => {
    if (disabled) return;
    setPickerFor(deckId);
    setFiles(null);
    try {
      const rows = await listMyMedia("file");
      setFiles(rows.filter(isSlideFile));
    } catch (err) {
      notice({ type: "error", title: "Could not load your files", subtext: errorMessage(err), duration: 7000 });
      setPickerFor(null);
    }
  };

  const onUpload = async (deck: SlideDeck | undefined, file: File | undefined) => {
    if (!deck || !file || disabled) return;
    setBusyId(deck.id);
    try {
      const pages = await guessPdfPages(file);
      const row = await uploadLearnMedia(file, "file");
      applyFile(deck, row, pages);
    } catch (err) {
      notice({ type: "error", title: "Could not upload the file", subtext: errorMessage(err), duration: 7000 });
    } finally {
      setBusyId(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="al-cb-decks">
      <div className="al-cb-questions-head">
        <h3 className="al-cb-item-section-title">
          <Presentation size={13} aria-hidden="true" />
          Slides
          <span className="al-cb-item-section-meta">· Canva or PDF</span>
        </h3>
        {mode && !disabled ? (
          <button type="button" className="al-cb-add-link" onClick={addDeck}>
            Add deck
          </button>
        ) : null}
      </div>

      {!mode ? (
        <div className="al-cb-deck-mode-cards" role="list">
          {MODE_CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <button
                key={card.id}
                type="button"
                className="al-cb-deck-mode-card"
                disabled={disabled}
                onClick={() => chooseMode(card.id)}
              >
                <span className="al-cb-deck-mode-card-icon">
                  <Icon size={14} aria-hidden="true" />
                </span>
                <span className="al-cb-deck-mode-card-title">{card.title}</span>
                <span className="al-cb-deck-mode-card-sub">{card.subtext}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="al-cb-deck-mode-tabs" role="tablist" aria-label="How these slides are used">
          {MODE_CARDS.map((card) => (
            <button
              key={card.id}
              type="button"
              role="tab"
              aria-selected={mode === card.id}
              className={`al-cb-deck-kind${mode === card.id ? " al-cb-deck-kind--on" : ""}`}
              disabled={disabled}
              onClick={() => chooseMode(card.id)}
            >
              {card.title}
            </button>
          ))}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        hidden
        accept={SLIDE_FILE_ACCEPT}
        disabled={disabled}
        onChange={(e) => {
          const deck = decks.find((row) => row.id === uploadFor);
          void onUpload(deck || decks[0], e.target.files?.[0]);
        }}
      />

      {mode && decks.length === 0 ? (
        <p className="al-cb-muted">
          {mode === "map"
            ? "Import a study, then map slide ranges onto units, lessons, or items."
            : "Import a Canva or PDF for this item."}
        </p>
      ) : null}

      {mode
        ? decks.map((deck) => {
        const canva = deck.kind === "canva" ? parseCanvaUrl(deck.url) : null;
        const busy = busyId === deck.id;
        const embed = canva?.embed || "";
        const shortPending = Boolean(canva?.short && !embed);

        return (
          <article key={deck.id} className="al-cb-deck-card">
            <div className="al-cb-deck-card-head">
              <input
                className="al-cb-input"
                placeholder="Title (optional)"
                value={deck.title}
                disabled={disabled}
                onChange={(e) => patch(deck.id, { title: e.target.value })}
              />
              {!disabled ? (
                <button
                  type="button"
                  className="al-cb-icon-btn al-cb-icon-btn--danger"
                  aria-label="Remove deck"
                  onClick={() => onChange(decks.filter((row) => row.id !== deck.id))}
                >
                  <Trash2 size={12} />
                </button>
              ) : null}
            </div>

            <div className="al-cb-deck-kinds" role="tablist" aria-label="Source type">
              {(["canva", "pdf"] as SlideKind[]).map((kind) => (
                <button
                  key={kind}
                  type="button"
                  role="tab"
                  aria-selected={deck.kind === kind || (kind === "pdf" && deck.kind === "pptx")}
                  className={`al-cb-deck-kind${deck.kind === kind || (kind === "pdf" && deck.kind === "pptx") ? " al-cb-deck-kind--on" : ""}`}
                  disabled={disabled}
                  onClick={() =>
                    patch(deck.id, {
                      kind,
                      url: kind === "canva" ? (deck.kind === "canva" ? deck.url : "") : deck.mediaId ? deck.url : "",
                      ...(kind === "canva" ? { mediaId: undefined, slideCount: deck.slideCount || 20 } : { kind }),
                    })
                  }
                >
                  {kindLabel(kind)}
                </button>
              ))}
            </div>

            {deck.kind === "canva" ? (
              <label className="al-cb-deck-link">
                <Link2 size={12} aria-hidden="true" />
                <input
                  className="al-cb-input"
                  placeholder="Canva share, embed, or canva.link"
                  value={deck.url}
                  disabled={disabled}
                  onChange={(e) => onCanvaUrl(deck.id, e.target.value)}
                  onBlur={() => void lookupCanva(deck.id, deck.url)}
                />
              </label>
            ) : (
              <div className="al-cb-deck-file">
                {deck.kind === "pptx" ? (
                  <p className="al-cb-muted">PowerPoint isn’t used on slides for now. Upload a PDF or switch to Canva.</p>
                ) : null}
                {deckHasSource(deck) ? (
                  <p className="al-cb-deck-file-name">{deck.title || kindLabel(deck.kind)} attached</p>
                ) : (
                  <p className="al-cb-muted">No file yet.</p>
                )}
                <div className="al-cb-deck-file-actions">
                  <button
                    type="button"
                    className="al-cb-add-link"
                    disabled={disabled || busy}
                    onClick={() => {
                      setUploadFor(deck.id);
                      fileRef.current?.click();
                    }}
                  >
                    {busy ? <Loader2 size={12} className="al-cb-spin" /> : <Upload size={12} />}
                    Upload PDF
                  </button>
                  <button
                    type="button"
                    className="al-cb-add-link"
                    disabled={disabled || busy}
                    onClick={() => void openPicker(deck.id)}
                  >
                    <FileText size={12} />
                    My files
                  </button>
                </div>
              </div>
            )}

            {shortPending && busy ? (
              <p className="al-cb-muted">Looking up Canva share…</p>
            ) : null}
            {deck.kind === "canva" && deck.url && !canva?.embed && !shortPending ? (
              <p className="al-cb-field-error">
                Paste a Canva share, public view, smart embed, or canva.link. Private editor links
                without a share token won’t embed.
              </p>
            ) : null}

            {embed ? (
              <div className="al-cb-deck-embed">
                <iframe title={deck.title || "Canva"} src={embed} allowFullScreen />
              </div>
            ) : deck.kind === "pdf" && deck.url ? (
              <div className="al-cb-deck-embed al-cb-deck-embed--pdf">
                <iframe title={deck.title || "PDF"} src={pdfViewerUrl(deck.url, 1)} />
              </div>
            ) : null}

            <label className="al-cb-deck-count">
              <span>
                Slides in this deck
                <span className="al-cb-deck-count-hint">
                  {deck.kind === "pdf" && deck.slideCount
                    ? "Read from the PDF when possible."
                    : "How many slides are in this file or Canva? We can’t always read it."}
                </span>
              </span>
              <input
                type="number"
                min={1}
                max={999}
                className="al-cb-input al-cb-input--tiny"
                value={deck.slideCount ?? ""}
                disabled={disabled}
                placeholder="20"
                onChange={(e) =>
                  patch(deck.id, { slideCount: e.target.value === "" ? undefined : Number(e.target.value) })
                }
              />
            </label>

            {mode === "map" ? (
            <div className="al-cb-deck-maps">
              {deck.maps.map((map) => (
                <div key={map.id} className="al-cb-deck-map">
                  <input
                    type="number"
                    min={1}
                    className="al-cb-input al-cb-input--tiny"
                    value={map.from}
                    disabled={disabled}
                    aria-label="From slide"
                    onChange={(e) =>
                      patchMap(deck, map.id, { ...map, from: Number(e.target.value) || 1 })
                    }
                  />
                  <span>–</span>
                  <input
                    type="number"
                    min={1}
                    className="al-cb-input al-cb-input--tiny"
                    value={map.to}
                    disabled={disabled}
                    aria-label="To slide"
                    onChange={(e) =>
                      patchMap(deck, map.id, { ...map, to: Number(e.target.value) || 1 })
                    }
                  />
                  <OutlinePicker
                    outline={outline}
                    selected={picksForMap(map, outline)}
                    disabled={disabled}
                    onChange={(picks) => patchMap(deck, map.id, applyPicks(map, picks))}
                  />
                  {!disabled ? (
                    <button
                      type="button"
                      className="al-cb-icon-btn al-cb-icon-btn--danger"
                      aria-label="Remove range"
                      onClick={() => patch(deck.id, { maps: deck.maps.filter((row) => row.id !== map.id) })}
                    >
                      <Trash2 size={12} />
                    </button>
                  ) : null}
                </div>
              ))}
              {!disabled ? (
                <button
                  type="button"
                  className="al-cb-add-link"
                  onClick={() => patch(deck.id, { maps: [...deck.maps, emptyMap()] })}
                >
                  Add range
                </button>
              ) : null}
            </div>
            ) : null}
          </article>
        );
      })
        : null}

      {pickerFor ? (
        <Dialog
          open
          title="My files"
          subtitle="PDFs you have uploaded"
          width="28rem"
          escapeClose={false}
          onClose={() => setPickerFor(null)}
        >
          {files == null ? (
            <p className="al-cb-muted">Loading…</p>
          ) : files.length === 0 ? (
            <p className="al-cb-muted">No PDFs yet. Upload one first.</p>
          ) : (
            <div className="al-cb-media-pick">
              {files.map((file) => (
                <button
                  key={file.id}
                  type="button"
                  className={`al-cb-media-pick-row${
                    file.id === decks.find((row) => row.id === pickerFor)?.mediaId
                      ? " al-cb-media-pick-row--on"
                      : ""
                  }`}
                  onClick={() => pickFile(file)}
                >
                  <span className="al-cb-media-pick-thumb al-cb-media-pick-thumb--icon">
                    <FileText size={16} />
                  </span>
                  <span className="al-cb-media-pick-name">{file.originalFilename || file.url}</span>
                  <span className="al-cb-media-pick-meta">
                    {new Date(file.createdAt).toLocaleDateString()}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Dialog>
      ) : null}
    </div>
  );
}
